/**
 * Shared analytics panel primitives — Style analytics and MRP analytics.
 * Keep pastel tokens and chrome here so the two panels do not diverge.
 */
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/** Pastel status palette — categorical. Do not reuse for ordinal price bands. */
export const tone = {
	published: "#A7D7C5",
	designerPending: "#AFC7E8",
	sourcingPending: "#F6C89F",
	mrpPending: "#CDBDE6",
	rejected: "#EFAFAF",
	neutral: "#E9ECEF",
};

/** Group series colours for stacked trend charts. */
export const seriesTones = [
	"#AFC7E8",
	"#A7D7C5",
	"#F6C89F",
	"#CDBDE6",
	"#EFAFAF",
	"#B9DCE5",
	"#E3D3A0",
	"#C9E0B6",
	"#E4BAD2",
	"#C3CADA",
	"#DCC8B9",
	"#AECBDE",
];

/**
 * Single-hue sequential ramp for ordinal price bands (<1k → 6k+).
 * Cool slate → deep indigo; not the categorical status palette.
 */
export const priceBandTones = ["#D6DEE8", "#B0BFD4", "#8499B8", "#5A7399", "#3A5275"];

export const ink = "#2E3338";
export const muted = "#8A929C";
export const line = "#EDEFF2";
export const panelBg = "#FAFAFB";

export const card = { border: `1px solid ${line}`, borderRadius: 2.5, background: "#fff", p: 2.5 };
export const eyebrow = {
	fontSize: 10.5,
	letterSpacing: "0.12em",
	textTransform: "uppercase",
	color: muted,
	fontWeight: 600,
};
export const colHead = {
	fontSize: 10,
	letterSpacing: "0.07em",
	textTransform: "uppercase",
	color: muted,
	fontWeight: 600,
};

export const nf = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));
export const days = (n) => (n == null ? "—" : `${n}d`);
export const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

export const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const monthLabel = (ym) => {
	const [y, m] = String(ym).split("-");
	return `${MONTHS[Number(m)] || ym} ${String(y).slice(2)}`;
};

/** Same eleven columns as stylesReport / mrp analytics FILTER_COLUMNS. */
export const ANALYTICS_SUPPORTED_FILTERS = new Set([
	"season",
	"brand",
	"sub_brand",
	"status",
	"stage",
	"category",
	"sub_category",
	"gender",
	"division",
	"order_type",
	"created_by",
]);

export const isActiveFilterValue = (value) => {
	if (value === undefined || value === null || value === "") return false;
	if (Array.isArray(value)) return value.length > 0;
	return true;
};

export const labelForFilter = (key) => String(key).replace(/_/g, " ");

export const splitAnalyticsFilters = (filter = {}) => {
	const active = Object.keys(filter || {}).filter((k) => isActiveFilterValue(filter[k]));
	return {
		applied: active.filter((k) => ANALYTICS_SUPPORTED_FILTERS.has(k)),
		ignored: active.filter((k) => !ANALYTICS_SUPPORTED_FILTERS.has(k)),
	};
};

export const KpiTile = ({ label, value, hint, accent, info }) => (
	<Box sx={{ ...card, flex: "1 1 0", minWidth: 0 }}>
		<Stack direction="row" alignItems="center" spacing={0.5}>
			<Typography sx={{ ...eyebrow, flex: 1, minWidth: 0 }}>{label}</Typography>
			{info ? (
				<Tooltip arrow title={info} enterDelay={200}>
					<InfoOutlinedIcon sx={{ fontSize: 14, color: muted, cursor: "help", flexShrink: 0 }} />
				</Tooltip>
			) : null}
		</Stack>
		<Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
			<Typography sx={{ fontSize: 28, fontWeight: 500, color: ink, lineHeight: 1.15 }}>{value}</Typography>
			{accent ? <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: accent, mb: 0.9 }} /> : null}
		</Stack>
		{hint ? <Typography sx={{ fontSize: 12, color: muted, mt: 0.25 }}>{hint}</Typography> : null}
	</Box>
);

export const Swatches = ({ items }) => (
	<Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
		{items.map((i) => (
			<Stack key={i.label} direction="row" alignItems="center" spacing={0.6}>
				<Box sx={{ width: 8, height: 8, borderRadius: 0.5, background: i.color }} />
				<Typography sx={{ fontSize: 11, color: muted }}>{i.label}</Typography>
			</Stack>
		))}
	</Stack>
);

export const MiniBars = ({ rows, labelKey, valueKey, suffix = "", color, dense = false }) => {
	const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
	const labelW = dense ? 36 : 84;
	const valueW = dense ? 28 : 44;
	const barH = dense ? 5 : 8;
	const fontSize = dense ? 10.5 : 12;
	return (
		<Stack spacing={dense ? 0.55 : 1.1} sx={{ mt: dense ? 1 : 1.5 }}>
			{rows.map((r) => (
				<Stack key={r[labelKey]} direction="row" alignItems="center" spacing={dense ? 0.75 : 1.5}>
					<Typography sx={{ width: labelW, fontSize, color: ink, flexShrink: 0 }} noWrap>
						{r[labelKey]}
					</Typography>
					<Box sx={{ flex: 1, height: barH, background: "#F5F6F8", borderRadius: 4, overflow: "hidden" }}>
						<Box sx={{ width: `${(100 * (Number(r[valueKey]) || 0)) / max}%`, height: "100%", background: color }} />
					</Box>
					<Typography sx={{ width: valueW, fontSize, color: muted, textAlign: "right", flexShrink: 0 }}>
						{nf(r[valueKey])}
						{suffix}
					</Typography>
				</Stack>
			))}
		</Stack>
	);
};

/** Horizontal stacked segments — status mix or price bands. */
export const StackedMixBar = ({ segments, total }) => {
	const denom = total || 1;
	return (
		<Box sx={{ flex: 1, minWidth: 90, display: "flex", height: 15, borderRadius: 1, overflow: "hidden", background: tone.neutral }}>
			{segments.map((seg) => {
				const value = Number(seg.value) || 0;
				if (!value) return null;
				return (
					<Tooltip key={seg.key} title={`${seg.label}: ${nf(value)}`} arrow>
						<Box sx={{ width: `${Math.max((100 * value) / denom, 1.5)}%`, background: seg.color }} />
					</Tooltip>
				);
			})}
		</Box>
	);
};

/**
 * Compact status “signal” — fixed-width mosaic of soft rounded blocks (not a stretched bar).
 * Only non-zero stages render; flex share = count. Hover for full breakdown.
 */
export const StatusMixSignal = ({ segments, total, width = 88 }) => {
	const active = (segments || []).filter((s) => Number(s.value) > 0);
	const tip = (segments || []).map((s) => `${s.label}: ${nf(s.value)}`).join(" · ");
	return (
		<Tooltip arrow title={tip || "No styles"}>
			<Box
				sx={{
					width,
					height: 18,
					flexShrink: 0,
					display: "flex",
					alignItems: "stretch",
					gap: "3px",
					p: "2px",
					borderRadius: 2,
					background: "#F4F6F8",
					boxSizing: "border-box",
					cursor: "help",
				}}>
				{active.length ? (
					active.map((seg) => (
						<Box
							key={seg.key}
							sx={{
								flex: Math.max(Number(seg.value) || 0, 0.01),
								minWidth: 5,
								borderRadius: 1.25,
								background: seg.color,
								boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45)",
							}}
						/>
					))
				) : (
					<Box sx={{ flex: 1, borderRadius: 1.25, background: tone.neutral }} />
				)}
			</Box>
		</Tooltip>
	);
};

export const DimensionToggle = ({ value, onChange, options, selectedTone = tone.designerPending }) => (
	<ToggleButtonGroup
		exclusive
		size="small"
		value={value}
		onChange={(_, v) => v && onChange(v)}
		sx={{
			"& .MuiToggleButton-root": {
				textTransform: "none",
				fontSize: 12,
				py: 0.35,
				px: 1.6,
				color: muted,
				border: `1px solid ${line}`,
			},
			"& .Mui-selected": { background: `${selectedTone} !important`, color: `${ink} !important` },
		}}>
		{options.map((d) => (
			<ToggleButton key={d.key} value={d.key}>
				{d.label}
			</ToggleButton>
		))}
	</ToggleButtonGroup>
);

/** Scoped-to text + amber chip for filters the endpoint cannot honour. */
export const ScopeChips = ({ applied, ignored, search, emptyLabel = "all styles" }) => (
	<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
		{applied.length || search ? (
			<Typography sx={{ fontSize: 11.5, color: muted, overflow: "hidden", textOverflow: "ellipsis" }} noWrap>
				scoped to {[search ? `search “${search}”` : null, ...applied.map(labelForFilter)].filter(Boolean).join(" · ")}
			</Typography>
		) : (
			<Typography sx={{ fontSize: 11.5, color: muted, flexShrink: 0 }}>{emptyLabel}</Typography>
		)}
		{ignored.length ? (
			<Tooltip
				arrow
				title={`These table filters cannot be applied here: ${ignored
					.map(labelForFilter)
					.join(", ")}. Figures above cover a wider set of styles than the table below.`}>
				<Box
					sx={{
						fontSize: 10.5,
						fontWeight: 600,
						letterSpacing: "0.04em",
						color: "#8A6A2F",
						background: "#FBF0DC",
						borderRadius: 5,
						px: 1,
						py: 0.25,
						cursor: "help",
						flexShrink: 0,
					}}>
					{ignored.length} filter{ignored.length > 1 ? "s" : ""} not applied
				</Box>
			</Tooltip>
		) : null}
	</Stack>
);
